import * as React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { StockRule, UpsellPreview } from './types';

// Formatting and the page drawing the Post-Purchase page and its A/B tests tab share.

export const pct = (part: number, whole: number) => (whole ? `${((part / whole) * 100).toFixed(1)}%` : '—');
export const usd = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
// Event times are stored in UTC; everything here reads in Pacific. Firebase
// times (an A/B test's startedAt) and Shopify's (an order's created_at)
// already carry their zone.
export const pacific = (utc: string) =>
  new Date(/(Z|[+-]\d\d:\d\d)$/.test(utc) ? utc : `${utc.replace(' ', 'T')}Z`).toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

// The API's own explanation (e.g. a 409: only production can switch dosaze.com).
export function errorText(error: unknown) {
  const response = (error as { response?: { data?: { error?: string; message?: string } } })?.response;
  return response?.data?.error ?? response?.data?.message ?? 'the request failed.';
}

// Upsell copy marks bold with **double asterisks** (collector upsells.js).
export const rich = (text: string | null) =>
  text?.split('**').map((part, index) => (index % 2 ? <strong key={index}>{part}</strong> : part)) ?? null;

export const usdCents = (cents: number) => `$${Number.isInteger(cents / 100) ? cents / 100 : (cents / 100).toFixed(2)}`;

// One upsell's page, drawn like Shopify draws the "standard" design: the grey
// banner, the product image beside the offer, the price breakdown, the two
// buttons. A close copy for the dashboard, not the checkout's own rendering.
// `phone`: drawn as Shopify draws it on a phone, everything in one column.
export function StandardPagePreview({ preview, phone = false }: { preview: Pick<UpsellPreview, 'display' | 'imageUrl' | 'imageDescription' | 'chargeCents'>; phone?: boolean }) {
  const display = preview.display!;
  const benefits = display.benefits?.length ? (
    <Stack spacing={0.75}>
      {display.benefits.map(benefit => (
        <Typography key={benefit} sx={{ fontSize: 15 }}>✓ {rich(benefit)}</Typography>
      ))}
    </Stack>
  ) : null;
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff', color: '#333', overflow: 'hidden' }}>
      {display.bannerTitle && (
        <Box sx={{ bgcolor: '#fafafa', borderBottom: '1px solid #e1e1e1', p: 2, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 0.5 }}>{display.bannerTitle}</Typography>
          <Typography sx={{ fontSize: 14, color: '#545454' }}>{rich(display.bannerBody)}</Typography>
        </Box>
      )}
      <Box sx={{ p: 3, display: 'grid', gap: 3, gridTemplateColumns: phone ? '1fr' : '1.6fr 1fr', alignItems: 'start' }}>
        {preview.imageUrl ? (
          <Box component="img" src={preview.imageUrl} alt={preview.imageDescription ?? ''} sx={{ width: '100%', display: 'block' }} />
        ) : <Box />}
        <Stack spacing={2}>
          {display.heading && <Typography sx={{ fontSize: 26, lineHeight: 1.3, color: '#545454' }}>{display.heading}</Typography>}
          {display.benefitsUnderHeading && benefits}
          {display.body && <Typography sx={{ fontSize: 15, color: '#707070' }}>{rich(display.body)}</Typography>}
          {!display.benefitsUnderHeading && benefits}
          <Stack spacing={1}>
            {display.lines.map((line, index) =>
              'separator' in line ? (
                <Box key={index} sx={{ borderTop: '1px solid #e1e1e1' }} />
              ) : (
                <Stack key={index} direction="row" justifyContent="space-between" spacing={2} sx={{ fontSize: 13, color: line.strike ? '#707070' : 'inherit' }}>
                  <span>{line.label}</span>
                  <Box component="span" sx={line.strike ? { textDecoration: 'line-through' } : { fontWeight: 600 }}>{line.value}</Box>
                </Stack>
              ),
            )}
            {/* The real page adds a Taxes line only when the order has tax; the total here says "+ tax". */}
            <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 17, fontWeight: 600 }}>
              <span>Added to your order</span>
              <span>{usdCents(preview.chargeCents ?? 0)} + tax</span>
            </Stack>
            {display.footnote && <Typography sx={{ fontSize: 13, color: '#707070' }}>{rich(display.footnote)}</Typography>}
          </Stack>
          <Box sx={{ bgcolor: '#1773b0', color: '#fff', borderRadius: '5px', py: 2, textAlign: 'center', fontWeight: 600 }}>{display.accept}</Box>
          <Box sx={{ border: '1px solid #d9d9d9', color: '#1773b0', borderRadius: '5px', py: 2, textAlign: 'center', fontWeight: 600 }}>{display.decline}</Box>
        </Stack>
      </Box>
    </Box>
  );
}

// Any US center is the API's default when none is set.
export const STOCK_RULE_LABELS: Record<StockRule, string> = {
  any: 'Any US center',
  nearest: 'Nearest center',
  region: 'Same region',
  off: 'No stock check',
};
